import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db, Routine, TrainingDay, TrainingDayExercise, ExercisePerformance } from '../database';
import { seedRoutines } from '../utils/seedData';

interface CarouselDayData {
  trainingDay: TrainingDay;
  exercises: string[];
  status: 'previous' | 'current' | 'next';
  exerciseDetails?: {
    exerciseId: number;
    name: string;
    weight: number;
    sets: { targetReps: number; order: number; }[];
  }[];
  lastWorkout?: {
    date: Date;
    sessionId: number;
    performances: ExercisePerformance[];
  };
  activeSession?: {
    id: number;
    startTime: Date;
    currentExerciseIndex: number;
    completedExercises: number[];
  };
}

const Training: React.FC = () => {
  const navigate = useNavigate();

  // Add CSS for hiding scrollbars
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .carousel-container::-webkit-scrollbar {
        display: none;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Home page state
  const [activeRoutines, setActiveRoutines] = useState<Routine[]>([]);
  const [hasRoutines, setHasRoutines] = useState(false);
  const [carouselData, setCarouselData] = useState<{ [key: number]: CarouselDayData[] }>({});

  // Routines page state
  const [routines, setRoutines] = useState<Routine[]>([]);
  const [trainingDays, setTrainingDays] = useState<TrainingDay[]>([]);
  const [showArchived, setShowArchived] = useState(false);

  const loadTrainingData = useCallback(async () => {
    try {
      // Load all data in parallel
      const [
        routinesData,
        trainingDaysData
      ] = await Promise.all([
        db.routines.orderBy('name').toArray(),
        db.trainingDays.toArray()
      ]);

      setRoutines(routinesData);
      setTrainingDays(trainingDaysData);
      setHasRoutines(routinesData.length > 0);

      // Filter for active routines (not archived)
      const activeRoutineList = routinesData.filter(r => r.active !== false && !r.archived);
      setActiveRoutines(activeRoutineList);

      // Build carousel data for each active routine
      const carouselDataMap: { [key: number]: CarouselDayData[] } = {};
      
      for (const routine of activeRoutineList) {
        const carouselDays = await buildCarouselData(routine, trainingDaysData);
        carouselDataMap[routine.id!] = carouselDays;
      }
      
      setCarouselData(carouselDataMap);
    } catch (error) {
      console.error('Failed to load training data:', error);
    }
  }, []);

  useEffect(() => {
    loadTrainingData();
  }, [loadTrainingData]);

  const buildCarouselData = async (routine: Routine, allTrainingDays: TrainingDay[]): Promise<CarouselDayData[]> => {
    const carouselDays: CarouselDayData[] = [];
    
    // Get the previous, current, and next training day indices
    const currentIndex = routine.currentDayIndex;
    const totalDays = routine.trainingDayIds.length;
    const prevIndex = currentIndex === 0 ? totalDays - 1 : currentIndex - 1;
    const nextIndex = currentIndex === totalDays - 1 ? 0 : currentIndex + 1;
    
    // Check if there are any completed workouts for this routine
    const hasCompletedWorkouts = await db.workoutSessions
      .where('routineId')
      .equals(routine.id!)
      .and(session => session.completed)
      .count() > 0;
    
    // Build data for days - only include previous if there are completed workouts
    const dayIndices = [];
    if (hasCompletedWorkouts) {
      dayIndices.push({ index: prevIndex, status: 'previous' as const });
    }
    dayIndices.push(
      { index: currentIndex, status: 'current' as const },
      { index: nextIndex, status: 'next' as const }
    );
    
    for (const { index, status } of dayIndices) {
      const trainingDayId = routine.trainingDayIds[index];
      const trainingDay = allTrainingDays.find(td => td.id === trainingDayId);
      
      if (trainingDay) {
        // Get exercises for this training day
        const trainingDayExercises = await db.trainingDayExercises
          .where('trainingDayId')
          .equals(trainingDayId)
          .toArray();
        
        const sortedExercises = trainingDayExercises.sort((a, b) => a.order - b.order);
        const exerciseNames = await Promise.all(
          sortedExercises.map(async (tde: TrainingDayExercise) => {
            const exercise = await db.exercises.get(tde.exerciseId);
            return exercise?.name || 'Unknown Exercise';
          })
        );
        
        // Get detailed exercise info for all tiles
        const exerciseDetails = await Promise.all(
          sortedExercises.map(async (tde: TrainingDayExercise) => {
            const exercise = await db.exercises.get(tde.exerciseId);
            return {
              exerciseId: tde.exerciseId,
              name: exercise?.name || 'Unknown Exercise',
              weight: tde.weight,
              sets: tde.sets
            };
          })
        );
        
        // Get last workout data for previous day
        let lastWorkout;
        if (status === 'previous') {
          const recentSessions = await db.workoutSessions
            .where('routineId')
            .equals(routine.id!)
            .and(session => session.trainingDayId === trainingDayId && session.completed)
            .reverse()
            .limit(1)
            .toArray();
          
          if (recentSessions.length > 0) {
            const session = recentSessions[0];
            const performances = await db.exercisePerformances
              .where('sessionId')
              .equals(session.id!)
              .toArray();
            
            lastWorkout = {
              date: session.startTime,
              sessionId: session.id!,
              performances: performances.sort((a, b) => a.order - b.order)
            };
          }
        }
        
        // Check for active session on current day
        let activeSession;
        if (status === 'current') {
          const activeSessions = await db.workoutSessions
            .where('routineId')
            .equals(routine.id!)
            .and(session => session.trainingDayId === trainingDayId && !session.completed)
            .toArray();
          
          if (activeSessions.length > 0) {
            const sessionData = activeSessions[0];
            const sessionId = sessionData.id!;
            
            // Get existing performances to determine completed exercises
            const existingPerformances = await db.exercisePerformances
              .where('sessionId')
              .equals(sessionId)
              .toArray();
            
            // Use saved currentExerciseIndex if available, otherwise fall back to completion logic
            let currentExerciseIndex;
            if (sessionData.currentExerciseIndex !== undefined) {
              currentExerciseIndex = sessionData.currentExerciseIndex;
            } else {
              // Fall back to finding next uncompleted exercise
              currentExerciseIndex = exerciseDetails.findIndex((exercise, index) => {
                const hasPerformance = existingPerformances.some(perf => 
                  perf.exerciseId === exercise.exerciseId && perf.order === index
                );
                return !hasPerformance;
              });
              currentExerciseIndex = currentExerciseIndex === -1 ? exerciseDetails.length - 1 : currentExerciseIndex;
            }
            
            // Get list of completed exercise indices
            const completedExercises = existingPerformances.map(perf => perf.order);
            
            activeSession = {
              id: sessionId,
              startTime: sessionData.startTime,
              currentExerciseIndex,
              completedExercises
            };
          }
        }

        carouselDays.push({
          trainingDay,
          exercises: exerciseNames,
          status,
          exerciseDetails,
          lastWorkout,
          activeSession
        });
      }
    }
    
    return carouselDays;
  };

  // Home page functions
  const startWorkout = async (routine: Routine) => {
    try {
      const nextDayId = routine.trainingDayIds[routine.currentDayIndex];
      if (!nextDayId) return;

      const trainingDay = await db.trainingDays.get(nextDayId);
      if (!trainingDay) return;

      const sessionId = await db.workoutSessions.add({
        routineId: routine.id!,
        trainingDayId: trainingDay.id!,
        startTime: new Date(),
        completed: false,
        createdAt: new Date()
      });

      navigate(`/workout/${sessionId}`);
    } catch (error) {
      console.error('Failed to start workout:', error);
    }
  };

  const continueSession = (sessionId: number) => {
    navigate(`/workout/${sessionId}`);
  };

  const viewWorkoutSummary = (sessionId: number) => {
    navigate(`/summary/${sessionId}`);
  };

  const abandonSession = async (sessionId: number) => {
    if (window.confirm('Are you sure you want to abandon this workout? All progress will be lost.')) {
      try {
        await db.workoutSessions.delete(sessionId);
        await loadTrainingData();
      } catch (error) {
        console.error('Failed to abandon workout session:', error);
      }
    }
  };

  const formatStartTime = (startTime: Date) => {
    const now = new Date();
    const diffTime = now.getTime() - startTime.getTime();
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();
    
    const timeString = startTime.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    
    if (startTime.toDateString() === today) {
      if (diffMinutes < 60) {
        return `Started ${diffMinutes}m ago`;
      } else {
        return `Started today at ${timeString}`;
      }
    } else if (startTime.toDateString() === yesterday) {
      return `Started yesterday at ${timeString}`;
    } else {
      return `Started ${startTime.toLocaleDateString()} at ${timeString}`;
    }
  };

  // Routines page functions
  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this routine?')) {
      try {
        await db.routines.delete(id);
        await loadTrainingData();
      } catch (error) {
        console.error('Failed to delete routine:', error);
      }
    }
  };

  const toggleActive = async (id: number, currentActive: boolean) => {
    try {
      await db.routines.update(id, { 
        active: !currentActive,
        updatedAt: new Date() 
      });
      await loadTrainingData();
    } catch (error) {
      console.error('Failed to update routine:', error);
    }
  };

  const handleSeedRoutines = async () => {
    try {
      await seedRoutines();
      await loadTrainingData();
    } catch (error) {
      console.error('Failed to seed routines:', error);
      alert('Failed to seed routines. Check console for details.');
    }
  };

  const handleArchive = async (id: number) => {
    try {
      await db.routines.update(id, {
        archived: true,
        active: false,
        updatedAt: new Date()
      });
      await loadTrainingData();
    } catch (error) {
      console.error('Failed to archive routine:', error);
    }
  };

  const handleRestore = async (id: number) => {
    try {
      await db.routines.update(id, {
        archived: false,
        updatedAt: new Date()
      });
      await loadTrainingData();
    } catch (error) {
      console.error('Failed to restore routine:', error);
    }
  };

  const getTrainingDayName = (id: number) => {
    return trainingDays.find(day => day.id === id)?.name || 'Unknown';
  };

  const getCurrentDay = (routine: Routine) => {
    if (routine.trainingDayIds.length === 0) return 'No training days';
    const currentDayId = routine.trainingDayIds[routine.currentDayIndex];
    return getTrainingDayName(currentDayId);
  };

  // Show setup flow for new users
  if (!hasRoutines) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 bg-clip-text text-transparent mb-4">
            💪 Sammy's Little Lifters 💪
          </h1>
          <div className="text-4xl mb-4">🏋️‍♂️</div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Welcome to Training
          </h2>
          <p className="text-gray-600">
            Let's set up your first routine
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">Quick Setup</h3>
          <p className="text-blue-700 text-sm mb-3">
            Create a routine in 3 easy steps, then start tracking workouts with minimal input.
          </p>
        </div>

        <div className="text-center space-y-4">
          <p className="text-gray-600 text-sm">
            Get started with your first routine:
          </p>
          
          <div className="flex flex-col gap-3">
            <Link
              to="/routines/new"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Create New Routine
            </Link>
            
            <button
              onClick={() => {
                // Call the global import function
                if ((window as any).importFromClipboard) {
                  (window as any).importFromClipboard();
                } else {
                  alert('Import function not available. Please refresh the page.');
                }
              }}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105 shadow-lg"
            >
              📋 Import from Clipboard
            </button>
            
            <button
              onClick={handleSeedRoutines}
              className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              📋 Load Sample Routines
            </button>
          </div>
        </div>

        <div className="mt-6 p-3 bg-green-50 rounded-lg text-center">
          <p className="text-green-800 text-sm">
            📱 Works completely offline • All data stays on your device
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-bold bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 bg-clip-text text-transparent">
          💪 Sammy's Little Lifters 💪
        </h1>
      </div>


      {/* Training Routines Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            Training Plans
          </h2>
          <div className="flex gap-2">
            <button
              onClick={() => {
                // Call the global import function
                if ((window as any).importFromClipboard) {
                  (window as any).importFromClipboard();
                } else {
                  alert('Import function not available. Please refresh the page.');
                }
              }}
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-3 py-2 rounded text-sm hover:from-purple-700 hover:to-pink-700 transition-all duration-200 shadow-md"
              title="Import routine from clipboard"
            >
              📋 Import
            </button>
            <Link
              to="/routines/new"
              className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors"
            >
              New Routine
            </Link>
          </div>
        </div>

        {/* Active Routines */}
        {activeRoutines.length > 0 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <span className="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
              Ready to Train
            </h3>
            {activeRoutines.map((routine) => {
                const carouselDays = carouselData[routine.id!] || [];
                const currentDay = carouselDays.find(day => day.status === 'current');

                return (
                  <div key={routine.id} className={`rounded-xl shadow-lg overflow-hidden ${
                    currentDay?.activeSession 
                      ? 'bg-gradient-to-br from-red-500 via-red-600 to-orange-700' 
                      : 'bg-gradient-to-br from-blue-500 via-blue-600 to-purple-700'
                  }`}>
                    <div className="p-6">
                      {/* Header - Routine Name with Edit Link */}
                      <div className="mb-4">
                        <div className="flex items-center justify-between">
                          <h4 className="text-2xl font-bold text-white truncate" title={routine.name}>
                            {routine.name}
                          </h4>
                          <Link 
                            to={`/routines/${routine.id}`} 
                            className="text-white/70 hover:text-white transition-colors"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        </div>
                      </div>

                      {/* Carousel Container */}
                      <div className="relative -mx-6 px-6">
                        <div 
                          className="flex overflow-x-auto snap-x snap-mandatory carousel-container" 
                          data-routine-id={routine.id}
                          style={{ 
                            scrollbarWidth: 'none', 
                            msOverflowStyle: 'none',
                            WebkitOverflowScrolling: 'touch'
                          }}
                          ref={(el) => {
                            // Auto-scroll to current day (middle position)
                            if (el && carouselDays.length > 0) {
                              const currentIndex = carouselDays.findIndex(day => day.status === 'current');
                              if (currentIndex !== -1) {
                                setTimeout(() => {
                                  el.scrollLeft = el.scrollWidth / 3 * currentIndex;
                                }, 100);
                              }
                            }
                          }}
                        >
                          {carouselDays
                            .sort((a, b) => {
                              const order = { previous: 0, current: 1, next: 2 };
                              return order[a.status] - order[b.status];
                            })
                            .map((dayData, index) => (
                            <div
                              key={`${dayData.trainingDay.id}-${dayData.status}`}
                              className="flex-shrink-0 snap-center px-2"
                              style={{ width: 'calc(100% - 40px)' }}
                            >
                              {/* Base tile structure for all three states */}
                              <div className="bg-white/5 rounded-lg p-6 backdrop-blur-sm h-80 relative overflow-hidden flex flex-col">
                                {dayData.status === 'current' && (
                                  <button
                                    onClick={() => dayData.activeSession ? continueSession(dayData.activeSession.id) : startWorkout(routine)}
                                    className="absolute inset-0 w-full h-full bg-gradient-to-b from-white/15 via-white/10 to-white/5 hover:from-white/20 hover:via-white/15 hover:to-white/10 rounded-lg transition-all group text-left"
                                  />
                                )}
                                
                                {dayData.status === 'previous' && dayData.lastWorkout && (
                                  <button
                                    onClick={() => viewWorkoutSummary(dayData.lastWorkout!.sessionId)}
                                    className="absolute inset-0 w-full h-full bg-gradient-to-b from-white/10 via-white/5 to-white/0 hover:from-white/15 hover:via-white/10 hover:to-white/5 rounded-lg transition-all group text-left"
                                  />
                                )}
                                
                                <div className="relative z-10 pointer-events-none flex flex-col h-full">
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-2 h-2 rounded-full ${
                                        dayData.status === 'previous' ? 'bg-green-400' :
                                        dayData.status === 'current' ? (dayData.activeSession ? 'bg-red-400 animate-pulse' : 'bg-yellow-400 animate-pulse') :
                                        'bg-blue-400'
                                      }`}></div>
                                      <span className={`text-xs ${
                                        dayData.status === 'current' ? 'text-white font-bold' : 'text-blue-200'
                                      }`}>
                                        {dayData.status === 'previous' ? 'Last completed' :
                                         dayData.status === 'current' ? (dayData.activeSession ? 'IN PROGRESS' : 'TODAY') :
                                         'Coming up'}
                                      </span>
                                    </div>
                                    
                                    {dayData.status === 'current' && dayData.activeSession && (
                                      <div className="flex items-center gap-1 text-xs text-white/80">
                                        <span>{formatStartTime(dayData.activeSession.startTime)}</span>
                                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M8 5v14l11-7z" />
                                        </svg>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className={`text-xl font-bold mb-4 truncate ${
                                    dayData.status === 'current' ? 'text-white' : 'text-blue-100'
                                  }`} title={dayData.trainingDay.name}>{dayData.trainingDay.name}</div>
                                  
                                  {dayData.status === 'previous' && (
                                    <div className="text-sm text-blue-200 mb-4">
                                      {dayData.lastWorkout ? 
                                        `${dayData.lastWorkout.date.toLocaleDateString()}` : 
                                        'No recent workout'
                                      }
                                    </div>
                                  )}
                                  
                                  <div className="flex-1 space-y-3 overflow-hidden">
                                    {dayData.exerciseDetails ? (
                                      <>
                                        {dayData.status === 'current' && dayData.activeSession ? (
                                          // Ultra-compact active workout preview
                                          <>
                                            {(() => {
                                              const currentIdx = dayData.activeSession!.currentExerciseIndex;
                                              const totalExercises = dayData.exerciseDetails.length;
                                              const currentExercise = dayData.exerciseDetails[currentIdx];
                                              const completedCount = dayData.activeSession!.completedExercises.length;
                                              
                                              return (
                                                <>
                                                  {/* Minimal progress text only */}
                                                  <div className="text-center mb-2">
                                                    <div className="text-xs text-white/70">
                                                      {completedCount}/{totalExercises} done
                                                    </div>
                                                  </div>

                                                  {/* Current exercise - ultra compact */}
                                                  {currentExercise && (
                                                    <div className="text-center">
                                                      <div className="text-white font-medium text-sm truncate mb-1" title={currentExercise.name}>
                                                        {currentExercise.name}
                                                      </div>
                                                      <div className="text-yellow-200 text-xs">
                                                        {currentExercise.weight}lbs
                                                      </div>
                                                    </div>
                                                  )}
                                                </>
                                              );
                                            })()}
                                          </>
                                        ) : (
                                          // Show exercise list - use actual performance data for previous workouts
                                          <>
                                            {dayData.exerciseDetails.slice(0, 3).map((exercise, exIndex) => {
                                              // For previous workouts, try to get actual weight from performance data
                                              let displayWeight = exercise.weight;
                                              let displayReps = exercise.sets.map(set => set.targetReps).join('-');
                                              
                                              if (dayData.status === 'previous' && dayData.lastWorkout) {
                                                const performance = dayData.lastWorkout.performances.find(p => p.exerciseId === exercise.exerciseId);
                                                if (performance) {
                                                  displayWeight = performance.weight;
                                                  displayReps = `${performance.finalSetActualReps} actual`;
                                                }
                                              }
                                              
                                              return (
                                                <div key={exIndex} className="text-sm">
                                                  <div className={`font-medium truncate ${
                                                    dayData.status === 'current' ? 'text-white' : 'text-blue-100'
                                                  }`} title={exercise.name}>{exercise.name}</div>
                                                  <div className={`text-xs ${
                                                    dayData.status === 'current' ? 'text-blue-100' : 'text-blue-300/70'
                                                  }`}>
                                                    {displayWeight}lbs • {displayReps} reps
                                                  </div>
                                                </div>
                                              );
                                            })}
                                            {dayData.exerciseDetails.length > 3 && (
                                              <div className={`text-xs italic ${
                                                dayData.status === 'current' ? 'text-blue-200' : 'text-blue-300/60'
                                              }`}>
                                                +{dayData.exerciseDetails.length - 3} more exercises...
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </>
                                    ) : (
                                      <>
                                        {dayData.exercises.slice(0, 3).map((exerciseName, exIndex) => (
                                          <div key={exIndex} className="text-sm text-blue-300/60 truncate" title={exerciseName}>
                                            {exerciseName}
                                          </div>
                                        ))}
                                        {dayData.exercises.length > 3 && (
                                          <div className="text-xs italic text-blue-300/60">
                                            +{dayData.exercises.length - 3} more exercises...
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Abandon button for active sessions */}
                        {currentDay?.activeSession && (
                          <div className="text-center mt-4">
                            <button
                              onClick={() => abandonSession(currentDay.activeSession!.id)}
                              className="text-red-400 hover:text-red-300 text-xs transition-colors"
                            >
                              Abandon Current Workout
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Inactive Routines */}
        {routines.filter(r => !r.active && !r.archived).length > 0 && (
          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900">Inactive Plans</h3>
            {routines.filter(r => !r.active && !r.archived).map((routine) => (
              <div
                key={routine.id}
                className="bg-white border rounded-lg p-4"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{routine.name}</h4>
                    <p className="text-gray-600 text-sm">
                      {routine.trainingDayIds.length} training days in cycle
                    </p>
                    <p className="text-gray-500 text-sm font-medium">
                      Next: {getCurrentDay(routine)}
                    </p>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => toggleActive(routine.id!, routine.active || false)}
                      className="bg-gray-400 text-white px-2 py-1 rounded text-xs"
                    >
                      Inactive
                    </button>
                    <Link
                      to={`/routines/${routine.id}/edit`}
                      className="text-blue-600 text-sm"
                    >
                      Edit
                    </Link>
                    <button
                      onClick={() => handleArchive(routine.id!)}
                      className="text-orange-600 text-sm"
                    >
                      Archive
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Archived Routines */}
        {routines.filter(r => r.archived).length > 0 && (
          <div className="space-y-3">
            <button
              onClick={() => setShowArchived(!showArchived)}
              className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
            >
              <span className={`transform transition-transform ${showArchived ? 'rotate-90' : ''}`}>
                ▶
              </span>
              <h3 className="font-semibold text-gray-900">
                Archived Plans ({routines.filter(r => r.archived).length})
              </h3>
            </button>
            
            {showArchived && (
              <div className="space-y-3 ml-4">
                {routines.filter(r => r.archived).map((routine) => (
                  <div
                    key={routine.id}
                    className="bg-gray-50 border border-gray-300 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-700">{routine.name}</h4>
                        <p className="text-gray-500 text-sm">
                          {routine.trainingDayIds.length} training days in cycle
                        </p>
                        <p className="text-gray-400 text-sm">
                          Archived routine
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 ml-4">
                        <button
                          onClick={() => handleRestore(routine.id!)}
                          className="text-blue-600 text-sm"
                        >
                          Restore
                        </button>
                        <button
                          onClick={() => handleDelete(routine.id!)}
                          className="text-red-600 text-sm"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* No Active Routines */}
        {activeRoutines.length === 0 && (
          <div className="text-center py-8 space-y-4">
            <div className="text-4xl mb-2">📋</div>
            <h3 className="text-lg font-semibold text-gray-900">No Active Training Plans</h3>
            <p className="text-gray-600 text-sm">
              Activate a routine below or create a new one to start training
            </p>
          </div>
        )}
      </div>

      {/* Offline Status */}
      <div className="text-center">
        <div className="inline-flex items-center px-3 py-1 bg-green-100 rounded-full">
          <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
          <span className="text-green-800 text-xs font-medium">Ready to work offline</span>
        </div>
      </div>
    </div>
  );
};

export default Training;